// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional permissions under AGPL-3.0 section 7 apply — see ADDITIONAL-TERMS.md.

/**
 * What you leave with. Three files, all generated in the tab:
 *
 *   rendered-prompt.txt   — the byte-exact string the model is fed
 *   token-budget.csv      — segment → tokens → share, one column per model
 *   messages.trimmed.json — the same conversation packed to fit, ready to send
 */

import type { ChatMessage, Segment, ToolDefinition } from './types';
import { log } from './eventlog';

export function csvEscape(v: string | number): string {
  const s = String(v);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export interface BudgetColumn {
  label: string;
  /** tokens per segment id */
  tokens: Map<string, number>;
  total: number;
  estimate: boolean;
}

/**
 * One row per segment, one column per model compared. The share column is of
 * the first model's total so the columns stay readable side by side.
 */
export function budgetCsv(segments: Segment[], columns: BudgetColumn[]): string {
  const head = ['segment', 'kind', 'role', 'characters', ...columns.flatMap((c) => [`${c.label} tokens`, `${c.label} share`])];
  const lines = [head.map(csvEscape).join(',')];

  for (const s of segments) {
    const row: Array<string | number> = [s.label, s.kind, s.role ?? '', s.chars];
    for (const c of columns) {
      const t = c.tokens.get(s.id) ?? 0;
      row.push(t, c.total ? `${((t / c.total) * 100).toFixed(2)}%` : '0%');
    }
    lines.push(row.map(csvEscape).join(','));
  }

  const totalRow: Array<string | number> = ['TOTAL', '', '', ''];
  for (const c of columns) totalRow.push(c.total, '100%');
  lines.push(totalRow.map(csvEscape).join(','));

  for (const c of columns) {
    if (c.estimate) {
      lines.push('');
      lines.push(
        csvEscape(
          `NOTE: the "${c.label}" column is an ESTIMATE, not an exact count. No public tokenizer exists for that model, so it is a proxy figure and can be materially low.`,
        ),
      );
    }
  }
  return `${lines.join('\n')}\n`;
}

export interface TrimmedExport {
  messages: ChatMessage[];
  tools?: ToolDefinition[];
}

export function trimmedJson(payload: TrimmedExport): string {
  return `${JSON.stringify(payload, null, 2)}\n`;
}

/** Save a string, preferring the file picker where the browser has one. */
export async function saveText(filename: string, text: string, mime = 'text/plain'): Promise<boolean> {
  const blob = new Blob([text], { type: `${mime};charset=utf-8` });
  const picker = (window as unknown as { showSaveFilePicker?: unknown }).showSaveFilePicker as
    | ((o: unknown) => Promise<{ createWritable(): Promise<{ write(b: Blob): Promise<void>; close(): Promise<void> }> }>)
    | undefined;

  if (typeof picker === 'function') {
    try {
      const handle = await picker({
        suggestedName: filename,
        types: [{ description: filename.split('.').pop()?.toUpperCase() ?? 'File', accept: { [mime]: [`.${filename.split('.').pop()}`] } }],
      });
      const w = await handle.createWritable();
      await w.write(blob);
      await w.close();
      log.export(`Saved ${filename} (${blob.size.toLocaleString()} bytes)`, 'ok');
      return true;
    } catch (e) {
      // AbortError means the user changed their mind; anything else falls back.
      if ((e as Error).name === 'AbortError') return false;
    }
  }

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
  log.export(`Downloaded ${filename} (${blob.size.toLocaleString()} bytes)`, 'ok');
  return true;
}

export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    log.export(`Copied ${text.length.toLocaleString()} characters to the clipboard`, 'ok');
    return true;
  } catch {
    log.export('The browser refused clipboard access.', 'warn');
    return false;
  }
}

export async function shareFiles(files: File[], title: string): Promise<boolean> {
  const nav = navigator as Navigator & { canShare?: (d: unknown) => boolean };
  if (!nav.share || !nav.canShare?.({ files })) return false;
  try {
    await nav.share({ files, title });
    log.export(`Shared ${files.length} file(s)`, 'ok');
    return true;
  } catch {
    return false;
  }
}
